import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// Flag de módulo para tratar a expiração de sessão apenas uma vez
// (evita uma enxurrada de toasts quando várias requisições falham com 401 ao mesmo tempo)
let sessionExpiryHandled = false;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configuração dinâmica de URL para compatibilidade com links compartilhados
  // Usar window.location quando REACT_APP_BACKEND_URL não estiver definido
  const getBackendUrl = () => {
    // Tentar obter da variável de ambiente (disponível em build time)
    const envUrl = process.env.REACT_APP_BACKEND_URL;
    if (envUrl) {
      return envUrl;
    }
    
    // Fallback para URL dinâmica baseada no domínio atual
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}//${window.location.host}`;
    }
    
    return '';
  };
  
  const BACKEND_URL = getBackendUrl();
  const API = `${BACKEND_URL}/api`;

  // Request interceptor: anexa o token atual (depende de `token`)
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [token]);

  // Response interceptor (registrado uma única vez): em 401 (sessão expirada/token inválido),
  // faz logout e redireciona ao login. Usa localStorage no guard (não o closure de `token`),
  // pois o axios fixa a cadeia de interceptors no momento do dispatch da requisição.
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const isAuthCall = url.includes('/api/auth/login') || url.includes('/api/auth/register');
        if (status === 401 && !isAuthCall) {
          // hadSession: havia um token (sessão) ou já estamos tratando a expiração
          const hadSession = !!localStorage.getItem('token') || sessionExpiryHandled;
          if (hadSession) {
            if (!sessionExpiryHandled) {
              sessionExpiryHandled = true;
              delete axios.defaults.headers.common['Authorization'];
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
              toast.error('Sua sessão expirou. Faça login novamente.');
            }
            // Engole o erro para os componentes não exibirem "Erro ao carregar dados"
            // (o usuário está sendo redirecionado ao login)
            return new Promise(() => {});
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Load token from localStorage on app start
  useEffect(() => {
    const init = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');

      if (savedToken && savedUser) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = async (email, senha) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        senha
      });

      const { access_token, user: userData } = response.data;

      // Nova sessão válida: rearmar o tratamento de expiração
      sessionExpiryHandled = false;
      
      // Anexar o token de forma sincrona para evitar race condition
      // (requisicoes feitas logo apos o login ja terao o header de Authorization)
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      toast.success(`Bem-vindo, ${userData.nome}!`);
      return true;
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(message);
      return false;
    }
  };

  const register = async (nome, email, senha, role = 'operacional') => {
    try {
      await axios.post(`${API}/auth/register`, {
        nome,
        email,
        senha,
        role
      });
      
      toast.success('Usuário criado com sucesso!');
      return true;
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar usuário';
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    toast.success('Logout realizado com sucesso');
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isOperacional = () => {
    return user?.role === 'operacional';
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isOperacional,
    isAuthenticated: !!token && !loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};